/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useTranslation } from "react-i18next";
import useSessionState from "../../hooks/useSessionState";
import apiClient from "../../helpers/apiClient";
import { DataTable, useTableFilters } from "../../components/data-table";

const Tolls = () => {
    const { t } = useTranslation("tolls"); // Added translation hook
    const [tollData, setTollData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useSessionState("tolls:page", 1);
    const [pageSize, setPageSize] = useSessionState("tolls:pageSize", 10);
    const [totalItems, setTotalItems] = useState(0);
    // const [isEzPassConnected, setIsEzPassConnected] = useState(true);
    const table = useTableFilters({ persistKey: "tolls", onChange: () => setCurrentPage(1) });
    const searchParams = table.filters;

    const fetchTollData = useCallback(async (page = 1, pageSizeParam = 10, searchParameters = {}) => {
        try {
            setLoading(true);

            // Build query string with search parameters
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: pageSizeParam.toString(),
                ...searchParameters,
            });

            const response = await apiClient.get(`api/toll/list?${queryParams.toString()}`);
            if (response.data.success) {
                setTollData(
                    response.data.data.map((toll, index) => ({
                        ...toll,
                        key: index,
                    })),
                );
                setTotalItems(response.data.meta?.total || 0);
            }
        } catch (_error) {
            // console.error("Error fetching toll data:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTollData(currentPage, pageSize, searchParams);
    }, [currentPage, pageSize, searchParams, fetchTollData]);

    const columns = [
        {
            title: t("columns.customerInfo"),
            dataIndex: "customerInfo",
            ...table.getColumnSearchProps("searchTerm"),
            width: 200,
            render: (customerInfo) => {
                if (customerInfo) {
                    return (
                        <div className="flex flex-col leading-tight">
                            <Link to={`/customers/${customerInfo?.userUID}`} className="sc-table-link">
                                {customerInfo?.firstName} {customerInfo?.lastName}
                            </Link>
                            <span className="text-xs text-gray-500">
                                {parsePhoneNumberFromString(`+${customerInfo.mobileNumber}`)?.formatNational() ??
                                    customerInfo.mobileNumber}
                            </span>
                        </div>
                    );
                }
                return t("common.notAvailable");
            },
        },
        {
            title: t("columns.plateNumber"),
            dataIndex: "carLinked",
            ...table.getColumnSearchProps("carLinked.carNumber"),
            width: 140,
            render: (car) => {
                if (car) {
                    return (
                        <Link to={`/cardetails/${car?.VIN}`} className="sc-table-link">
                            {car.carNumber || t("common.notAvailable")}
                        </Link>
                    );
                }
                return t("common.notAvailable");
            },
        },
        {
            title: t("columns.reservationId"),
            dataIndex: "reservationId",
            ...table.getColumnSearchProps("reservationId"),
            width: 150,
            render: (id) => {
                if (id) {
                    return (
                        <Link to={`/reservations/${id}`} className="sc-table-link sc-mono">
                            {id}
                        </Link>
                    );
                }
                return t("common.notAvailable");
            },
        },
        {
            title: t("columns.amount"),
            dataIndex: "amount",
            align: "right",
            render: (amount) => `$${Math.abs(amount).toFixed(2)}`,
        },
        {
            title: t("columns.exitTime"),
            render: (_, record) => {
                const date = new Date(record.transaction_date).toLocaleDateString();
                return (
                    <div className="flex flex-col leading-tight">
                        <span className="text-sm">{date}</span>
                        <span className="text-xs text-gray-500">{record.exit_time}</span>
                    </div>
                );
            },
        },
        {
            title: t("columns.exitPlaza"),
            width: 240,
            ellipsis: true,
            render: (_, record) => {
                if (!record.exit_plaza) return t("common.notAvailable");
                // Distance-based roads (e.g. NYS Thruway) have an entry plaza too
                const codes = record.entry_plaza ? `${record.entry_plaza} → ${record.exit_plaza}` : record.exit_plaza;
                const names = record.entry_plaza_name
                    ? `${record.entry_plaza_name} → ${record.exit_plaza_name}`
                    : record.exit_plaza_name;
                return (
                    <div className="flex flex-col leading-tight">
                        <span className="text-sm">{codes}</span>
                        {names && <span className="text-xs text-gray-500">{names}</span>}
                    </div>
                );
            },
        },
        {
            title: "Lane Txn ID",
            render: (_, record) => (record.laneTxnId ? <p>{record.laneTxnId}</p> : t("common.notAvailable")),
        },
    ];

    return (
        <>
            <div className="flex-1 min-h-0">
                <div className="carscont">
                    <div className="carsinfocont fontRegular flex flex-col">
                        <DataTable
                            title={t("pageTitle")}
                            count={totalItems}
                            filters={table}
                            search={{ key: "searchTerm", placeholder: t("searchPlaceholder") }}
                            filterLabels={{
                                searchTerm: t("columns.customerInfo"),
                                "carLinked.carNumber": t("columns.plateNumber"),
                                reservationId: t("columns.reservationId"),
                            }}
                            columns={columns}
                            dataSource={tollData}
                            loading={loading}
                            pagination={{
                                total: totalItems,
                                current: currentPage,
                                pageSize,
                                onChange: setCurrentPage,
                                onPageSizeChange: setPageSize,
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Tolls;
